import type { RoiSample } from "./sample";

export const formatMoney = (amount: number, currency: string, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currencyDisplay: "narrowSymbol",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);

// "£384K": the short form the ring's centre has room for.
export const formatCompactMoney = (
  amount: number,
  currency: string,
  locale: string
) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currencyDisplay: "narrowSymbol",
    currency,
    notation: "compact",
    maximumFractionDigits: 0,
  }).format(amount);

export const formatPercent = (percent: number, locale: string) =>
  new Intl.NumberFormat(locale, { style: "percent" }).format(percent / 100);

// "4.2": always one decimal, so a projection that falls to zero reads "0.0".
export const formatMultipleOneDecimal = (multiple: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(multiple);

export const formatMultiple = (multiple: number, locale: string) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(multiple);

// "2025-01" as "January 2025" in the viewer's language.
export const formatMonth = (month: string, locale: string) => {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) throw new Error(`Not a YYYY-MM month: ${month}`);
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
};

// "24 April" / "1 August 2026": a calendar day, "YYYY-MM-DD", in the viewer's
// language.
const formatDay = (
  day: string,
  locale: string,
  options: Intl.DateTimeFormatOptions
) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) throw new Error(`Not a YYYY-MM-DD day: ${day}`);
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: "UTC",
  }).format(
    new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  );
};

export const formatDayMonth = (day: string, locale: string) =>
  formatDay(day, locale, { day: "numeric", month: "long" });

export const formatFullDay = (day: string, locale: string) =>
  formatDay(day, locale, { day: "numeric", month: "long", year: "numeric" });

export const formatNumber = (value: number, locale: string) =>
  new Intl.NumberFormat(locale).format(value);

// "27.1%": a percentage the sample holds as a figure already out of 100.
export const formatPercentOneDecimal = (percent: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 1,
  }).format(percent / 100);

// "+21.3%" / "-27.1%": the direction of a change, in the viewer's format.
export const formatSignedPercent = (percent: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  }).format(percent / 100);

// The scenario modeller's straight line through zero: the sample's return and
// savings scale with participation, relative to where participation is now.
export const projectScenario = (
  sample: Pick<RoiSample, "roiMultiple" | "totalSavings" | "scenarioModeller">,
  participationPercent: number
) => {
  const ratio =
    participationPercent / sample.scenarioModeller.currentParticipationPercent;
  return {
    roiMultiple: sample.roiMultiple * ratio,
    savings: Math.round(sample.totalSavings.amount * ratio),
  };
};
